/**
 * Service for detecting whether the current browser can natively render HEIC/HEIF images.
 *
 * Uses a probe-based approach: creates a tiny 1x1 HEIC image as a data URI and
 * attempts to load it in an <img> element. If the browser's native decoder handles
 * it (onload fires with naturalWidth > 0), HEIC is supported — meaning the browser
 * can render HEIC directly without client-side conversion, preserving HDR, wide
 * gamut (P3), and 10-bit depth.
 *
 * Result is cached after first check (runs once per session).
 */

// Minimal valid 1x1 HEIC image encoded as base64 (ISOBMFF container with HEVC still image)
// This is the smallest possible valid HEIC file that image decoders will accept.
const HEIC_PROBE_DATA_URI =
  'data:image/heic;base64,' +
  'AAAAGGZ0eXBoZWljAAAAAG1pZjFoZWljAAAA' +
  'jW1ldGEAAAAAAAAAIWhkbHIAAAAAAAAAAHBp' +
  'Y3QAAAAAAAAAAAAAAAAAAAAADnBpdG0AAAAA' +
  'AQAAABppaWxvYwAAAABEAAABAAEAAAABAAAA' +
  'GgAAACNpaW5mAAAAAAABAAAAFWluZmUCAAEA' +
  'AQBhdmMxAAAAABhpcHJwAAAAEGlwY28AAAAA' +
  'AAAAFGlwbWEAAAAAAAAAAQECAf//AAAAGm1k' +
  'YXQ=';

let cachedResult: boolean | null = null;
let pendingCheck: Promise<boolean> | null = null;

/**
 * Checks whether the current browser can natively decode and display HEIC images.
 * Returns a cached result on subsequent calls.
 *
 * @returns Promise<boolean> — true if HEIC is natively supported
 */
export async function checkHeicSupport(): Promise<boolean> {
  // Return cached result if available
  if (cachedResult !== null) {
    return cachedResult;
  }

  // Avoid duplicate probes if called concurrently
  if (pendingCheck) {
    return pendingCheck;
  }

  pendingCheck = new Promise<boolean>((resolve) => {
    const img = new Image();
    const timeoutMs = 3000;

    const timer = setTimeout(() => {
      // Timeout — assume no HEIC support
      cleanup();
      cachedResult = false;
      console.log('HEIC support detection: timed out — assuming not supported');
      resolve(false);
    }, timeoutMs);

    const cleanup = () => {
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
    };

    img.onload = () => {
      cleanup();
      const supported = img.naturalWidth > 0 && img.naturalHeight > 0;
      cachedResult = supported;
      console.log(`HEIC support detection: ${supported ? 'supported' : 'not supported'} (native rendering)`);
      resolve(supported);
    };

    img.onerror = () => {
      cleanup();
      cachedResult = false;
      console.log('HEIC support detection: not supported (decode error)');
      resolve(false);
    };

    img.src = HEIC_PROBE_DATA_URI;
  });

  const result = await pendingCheck;
  pendingCheck = null;
  return result;
}

/**
 * Returns the cached HEIC support result, or null if detection hasn't completed yet.
 * Useful for synchronous checks after initial detection.
 */
export function getHeicSupportCached(): boolean | null {
  return cachedResult;
}
